import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Link, Paper, Snackbar, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { themePalette } from '../../utilities/branding';
import { Strings } from '../../assets/common/strings';
import { AppConfig } from '../../types';

// Session-scoped so the banner reappears on each new browser session but not on refresh (per deck slide 10).
const SESSION_KEY = 'ac_privacy_banner_dismissed';
// Public, permission-less terms page so the links resolve on the pre-login page.

const useStyles = makeStyles(() => ({
	snackbar: {
		maxWidth: 720,
	},
	paper: {
		display: 'flex',
		alignItems: 'flex-start',
		padding: '12px 16px',
		backgroundColor: themePalette.default_background,
		color: themePalette.primary_text,
		borderTop: `4px solid ${themePalette.primary}`,
	},
	message: {
		flex: 1,
		fontSize: 13,
		lineHeight: 1.5,
	},
	link: {
		color: themePalette.primary,
		textDecoration: 'underline',
	},
	close: {
		marginLeft: 8,
		marginTop: -4,
		color: themePalette.sub_text,
	},
}));

const hasDismissed = (): boolean => {
	try {
		return window.sessionStorage.getItem(SESSION_KEY) === 'true';
	} catch {
		return false;
	}
};

const PrivacyBanner: React.FC = () => {
	const classes = useStyles();
	const [open, setOpen] = useState<boolean>(() => !hasDismissed());

	const handleClose = () => {
		try {
			window.sessionStorage.setItem(SESSION_KEY, 'true');
		} catch {
			// sessionStorage unavailable; dismissal simply won't persist for the session
		}
		setOpen(false);
	};

	return (
		<Snackbar
			open={open}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			className={classes.snackbar}
			disableWindowBlurListener
		>
			<Paper
				elevation={6}
				className={classes.paper}
				role="region"
				aria-label={Strings.PrivacyBanner.Region}
			>
				<Typography component="p" className={classes.message}>
					{Strings.PrivacyBanner.MessageStart}
					<Link
						href={AppConfig.privacy_policy_link}
						target="_blank"
						rel="noopener noreferrer"
						className={classes.link}
					>
						{Strings.PrivacyBanner.PrivacyPolicy}
					</Link>
					{Strings.PrivacyBanner.MessageConjunction}
					<Link
						href={AppConfig.terms_of_use_link}
						target="_blank"
						rel="noopener noreferrer"
						className={classes.link}
					>
						{Strings.PrivacyBanner.TermsOfUse}
					</Link>
					{Strings.PrivacyBanner.MessageEnd}
				</Typography>
				<IconButton
					size="small"
					aria-label={Strings.PrivacyBanner.Close}
					onClick={handleClose}
					className={classes.close}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Paper>
		</Snackbar>
	);
};

export const PrivacyBannerComponent = PrivacyBanner;
